import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import React, { useState, useEffect } from 'react';
import { firebaseService } from '../services/firebaseService';
import { apiService } from '../services/apiService';
import { notificationManager } from '../services/notification';
import { posthogService } from '../services/posthogService';

export const Route = createFileRoute('/recommendations')({
  component: RecommendationsPage,
});

function RecommendationsPage() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const SESSIONS_PER_PAGE = 5;

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const currentUser = await firebaseService.getCurrentUser();
      
      if (!currentUser) {
        notificationManager.error('Please sign in to view your recommendations');
        navigate({ to: '/' });
        return;
      }

      const idToken = await firebaseService.getIdToken();
      const sessionsData = await apiService.getUserSessions(idToken);
      
      setSessions(sessionsData);
      
      // Track recommendations list viewed
      posthogService.recommendationsListViewed(sessionsData.length);
    } catch (error) {
      console.error('Error loading sessions:', error);
      notificationManager.error(error.message || 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Check if it's today
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Check if it's yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Otherwise show full date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className='bg-white rounded-2xl p-8 shadow-xl text-center'>
        <div className='flex items-center justify-center gap-3'>
          <span className='text-sm text-gray-600'>Loading recommendations...</span>
          <div className='w-4 h-4 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin'></div>
        </div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className='bg-white rounded-2xl p-8 shadow-xl'>
        <div className='max-w-md mx-auto text-center'>
          <div className='text-sm text-gray-500 mb-2 uppercase tracking-wide'>
            No Recommendations Yet
          </div>
          <h2 className='text-2xl font-light text-gray-900 mb-4'>
            Start Your First Questionnaire
          </h2>
          <p className='text-sm text-gray-600 mb-8'>
            Complete the questionnaire to get personalized credit card recommendations tailored to your needs.
          </p>
          <Link
            to='/'
            className='inline-block py-3 px-6 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors'
          >
            Start Questionnaire
          </Link>
        </div>
      </div>
    );
  }

  // Filter out incomplete sessions and separate processing from others
  const processingSessions = sessions.filter(s => s.status === 'processing');
  const otherSessions = sessions.filter(s => s.status !== 'processing' && s.status !== 'incomplete');
  
  // Pagination for other sessions
  const totalPages = Math.ceil(otherSessions.length / SESSIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * SESSIONS_PER_PAGE;
  const endIndex = startIndex + SESSIONS_PER_PAGE;
  const paginatedSessions = otherSessions.slice(startIndex, endIndex);

  const renderSessionCard = (session, showLoading = false) => (
    <Link
      key={session.session_id}
      to={`/analysis/${session.session_id}`}
      search={{ from: 'list', status: session.status }}
      className='block border border-gray-200 rounded-lg p-5 hover:border-gray-900 hover:bg-gray-50 transition-all group'
    >
      <div className='flex items-center justify-between gap-4'>
        {/* Left: Date and Status */}
        <div className='flex-1 min-w-0'>
          <div className='text-base font-light text-gray-900 mb-1'>
            {formatDate(session.created_at)}
          </div>
          
          {session.status === 'done' && (
            <div className='text-sm text-gray-600'>
              {session.recommendations_count} {session.recommendations_count === 1 ? 'recommendation' : 'recommendations'}
            </div>
          )}
          
          {session.status === 'processing' && (
            <div className='flex items-center gap-2 text-sm text-blue-600'>
              <div className='w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin'></div>
              <span>Generating recommendations...</span>
            </div>
          )}
          
          {session.status === 'incomplete' && (
            <div className='text-sm text-gray-500'>
              Questionnaire in progress
            </div>
          )}
          
          {session.status === 'error' && (
            <div className='text-sm text-red-600'>
              Generation failed
            </div>
          )}
        </div>

        {/* Right: Arrow */}
        <div className='text-gray-400 group-hover:text-gray-900 transition-colors flex-shrink-0'>
          <svg
            className='w-5 h-5'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M9 5l7 7-7 7'
            />
          </svg>
        </div>
      </div>
    </Link>
  );

  return (
    <div className='bg-white rounded-2xl p-8 shadow-xl'>
      <div className='max-w-3xl mx-auto'>
        {/* Header */}
        <div className='mb-8'>
          <h2 className='text-2xl font-light text-gray-900 mb-2'>
            My Recommendations
          </h2>
          <p className='text-sm text-gray-600'>
            Your credit card analysis history
          </p>
        </div>

        {/* All Sessions List */}
        <div className='space-y-3 mb-8'>
          {/* Processing sessions first */}
          {processingSessions.map(session => renderSessionCard(session, true))}
          
          {/* Then paginated other sessions */}
          {paginatedSessions.map(session => renderSessionCard(session))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className='flex items-center justify-center gap-2'>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className='px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
            >
              ← Previous
            </button>
            
            <div className='flex items-center gap-2 px-4'>
              <span className='text-sm text-gray-600'>
                Page {currentPage} of {totalPages}
              </span>
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className='px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecommendationsPage;

